import axios from 'axios';
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import {
    DEFAULT_LANGUAGE,
    LOCALE_STORAGE_KEY,
    SUPPORTED_LANGUAGES,
    normalizeLocale,
} from './languages';
import {
    getInitialLanguage,
    getFallbackLng,
    getDetectionOptions,
    initDomainLanguage,
    markUserLanguageChoice,
    persistLanguageStorage,
} from '../utils/domainLanguage';
import { applyDocumentDirection } from './documentDirection';

import ru from './locales/ru.json';
import en from './locales/en.json';
import hy from './locales/hy.json';
import tr from './locales/tr.json';
import de from './locales/de.json';
import fr from './locales/fr.json';
import es from './locales/es.json';
import pl from './locales/pl.json';
import cs from './locales/cs.json';
import ptBR from './locales/pt-BR.json';
import ar from './locales/ar.json';
import az from './locales/az.json';
import ka from './locales/ka.json';
import kk from './locales/kk.json';
import uz from './locales/uz.json';
import aboutRu from './locales/ru/about.json';
import aboutEn from './locales/en/about.json';
import betaRu from './locales/ru/beta.json';
import betaEn from './locales/en/beta.json';
import featuresRu from './locales/ru/features.json';
import featuresEn from './locales/en/features.json';
import featureListRu from './locales/ru/featureList.json';
import featureListEn from './locales/en/featureList.json';
import orgSettingsRu from './locales/ru/orgSettings.json';
import orgSettingsEn from './locales/en/orgSettings.json';
import platformSettingsRu from './locales/ru/platformSettings.json';
import platformSettingsEn from './locales/en/platformSettings.json';
import connectionRu from './locales/ru/connection.json';
import connectionEn from './locales/en/connection.json';
import regionSelectRu from './locales/ru/regionSelect.json';
import regionSelectEn from './locales/en/regionSelect.json';
import adminConfigRu from './locales/ru/adminConfig.json';
import adminConfigEn from './locales/en/adminConfig.json';
import paymentRu from './locales/ru/payment.json';
import paymentEn from './locales/en/payment.json';
import applyRu from './locales/ru/apply.json';
import applyEn from './locales/en/apply.json';
import notificationsRu from './locales/ru/notifications.json';
import notificationsEn from './locales/en/notifications.json';

/** Namespaces = top-level keys in locale JSON (backward compatible with useTranslation('nav')). */
export const NAMESPACES = [
    'common', 'nav', 'landing', 'features', 'featureList', 'betaOffer', 'dashboard', 'admin', 'auth', 'profile',
    'students', 'groups', 'schedule', 'payments', 'payment', 'apply', 'settings',
    'coach', 'finance', 'errors', 'messages', 'tabs', 'school', 'events', 'scheduleManager', 'pricingPage',
    'schoolDiscovery', 'sportTypeSelector', 'schoolVenues', 'demoTour', 'eventFinance', 'consent',
    'cashier', 'reports', 'balance', 'billing', 'platformBilling', 'platformSettings', 'connection', 'regionSelect', 'subscriptionManager', 'inventoryManage', 'nomenclature', 'applications', 'applyPage', 'rentalPage', 'uploadPage', 'loyaltyPage', 'importHistory', 'importStudents', 'activeStudents', 'schoolSwitcher', 'plannedSessions', 'scheduleSessionDetail', 'notificationInbox', 'parentMobile', 'demoView', 'demo', 'wardSwitcher', 'guardianLink', 'notifications', 'ownerMobile', 'ownerForms', 'coachMobile',     'schoolSettings', 'orgSettings', 'legalDocs', 'about', 'beta', 'student', 'sharedMobile', 'schoolIntegrations', 'venueEquipment', 'staffManager', 'onboarding', 'registerPage',     'adminEmailSettings', 'attendanceCalendar', 'adminReferences', 'crossSchool', 'demoCatalog', 'adminI18n', 'adminConfig', 'bugs', 'adminBugs', 'adminNotifications', 'marketplace',
];

function bundleToResources(flat) {
    const resources = { translation: flat };
    for (const ns of NAMESPACES) {
        if (flat[ns] && typeof flat[ns] === 'object') {
            resources[ns] = flat[ns];
        }
    }
    return resources;
}

const localeBundles = {
    ru, en, hy, tr, de, fr, es, pl, cs, 'pt-BR': ptBR, ar, az, ka, kk, uz,
};

const resources = {};
for (const [code, bundle] of Object.entries(localeBundles)) {
    resources[code] = bundleToResources(bundle);
}
if (resources.ru) {
    resources.ru.orgSettings = orgSettingsRu;
    resources.ru.about = aboutRu;
    resources.ru.beta = betaRu;
    resources.ru.features = featuresRu;
    resources.ru.featureList = featureListRu;
    resources.ru.platformSettings = platformSettingsRu;
    resources.ru.connection = connectionRu;
    resources.ru.regionSelect = regionSelectRu;
    resources.ru.adminConfig = adminConfigRu;
    resources.ru.payment = paymentRu;
    resources.ru.apply = applyRu;
    resources.ru.notifications = notificationsRu;
}
if (resources.en) {
    resources.en.orgSettings = orgSettingsEn;
    resources.en.about = aboutEn;
    resources.en.beta = betaEn;
    resources.en.features = featuresEn;
    resources.en.featureList = featureListEn;
    resources.en.platformSettings = platformSettingsEn;
    resources.en.connection = connectionEn;
    resources.en.regionSelect = regionSelectEn;
    resources.en.adminConfig = adminConfigEn;
    resources.en.payment = paymentEn;
    resources.en.apply = applyEn;
    resources.en.notifications = notificationsEn;
}

initDomainLanguage();

const supportedLngs = SUPPORTED_LANGUAGES.map((l) => l.code);
const initialLang = getInitialLanguage();

i18n
    .use(LanguageDetector)
    .use(initReactI18next)
    .init({
        resources,
        lng: initialLang,
        fallbackLng: getFallbackLng(),
        supportedLngs,
        nonExplicitSupportedLngs: true,
        load: 'currentOnly',
        ns: ['translation', ...NAMESPACES],
        defaultNS: 'translation',
        fallbackNS: 'translation',
        keySeparator: '.',
        nsSeparator: ':',
        interpolation: { escapeValue: false },
        detection: getDetectionOptions(),
        react: { useSuspense: false, bindI18n: 'languageChanged', bindI18nStore: 'added removed' },
    });

function syncAcceptLanguage(lng) {
    const code = normalizeLocale(lng || DEFAULT_LANGUAGE);
    axios.defaults.headers.common['Accept-Language'] = code;
}

/** Every axios call (including axios.create instances) sends the active UI language. */
axios.interceptors.request.use((config) => {
    const headers = config.headers ?? {};
    const code = normalizeLocale(i18n.resolvedLanguage || i18n.language || DEFAULT_LANGUAGE);
    if (typeof headers.set === 'function') {
        headers.set('Accept-Language', code);
        headers.set('X-Language', code);
    } else {
        headers['Accept-Language'] = code;
        headers['X-Language'] = code;
    }
    config.headers = headers;
    return config;
});

applyDocumentDirection(initialLang);
syncAcceptLanguage(initialLang);

i18n.on('languageChanged', (lng) => {
    const code = normalizeLocale(lng);
    persistLanguageStorage(code);
    applyDocumentDirection(lng);
    syncAcceptLanguage(lng);
    if (typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('sp:locale-changed', { detail: { language: code } }));
    }
});

export { markUserLanguageChoice };
export default i18n;
